import React, { useState, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useApiClient } from '../../hooks/useApiClient'
import { useDisponibilidadSimple } from '../../hooks/useEspacios'
import {
  useCrearReservaManual,
  useDTEsReserva,
  useTiposEspacios,
} from '../../hooks/useReservasManual'
import DateAvailabilityModal from '../../components/booking/DateAvailabilityModal'
import ComprobanteViewer from '../../components/common/ComprobanteViewer'

import { parseLocalDateString } from '../../utils/dateHelpers'

interface ClienteData {
  nombre: string
  email: string
  telefono: string
  numero_documento: string
  tipo_documento: string
}

interface EspacioSeleccionado {
  id: string
  nombre: string
  codigo: string
  tipoNombre: string
  tarifaBase: number
  monto: number
  // ✅ NUEVO: Cada espacio tiene sus propias fechas
  fechaEntrada: string
  fechaSalida: string
  unidadReserva: string // 'dia' o 'noche'
  desglose?: Array<{
    fecha: string
    dia_semana?: string
    tarifa: number
  }>
}

interface EspacioFechasData {
  // Búsqueda de disponibilidad
  tipoEspacioId: string
  tipoEspacioNombre: string
  fechaEntrada: string // ✅ Fecha inicio para búsqueda
  fechaSalida: string // ✅ Fecha fin para búsqueda

  // Espacios seleccionados (carrito)
  espaciosSeleccionados: EspacioSeleccionado[] // Cada uno con sus propias fechas

  // Metadatos
  tarifaBase: number
  unidadReserva: string // Del tipo (para referencia)
}

interface PagoData {
  metodoPago: 'transferencia' | 'efectivo' | 'cheque' | 'tarjeta'
  numeroPago: string
  fechaPago: string
  montoPago: number // Monto a pagar (puede ser parcial)
  comprobante: File | null
}

interface DTEData {
  crearDTE: boolean
  tipoDTE: 'boleta' | 'factura' | 'nota_credito'
  numeroDTE: string
  folioInterno: string
  montoDTE: number // Monto del DTE (puede diferir del pago)
  fechaVencimiento: string
}

interface CreatedPagoInfo {
  id: number
  filename?: string
  mimeType?: string
  size?: number
  uploadDate?: string
}

export const CreateReservaManualPage: React.FC = () => {
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const { apiClient } = useApiClient()
  const [step, setStep] = useState(1)

  // Estado del formulario
  const [clienteData, setClienteData] = useState<ClienteData>({
    nombre: '',
    email: '',
    telefono: '',
    numero_documento: '',
    tipo_documento: 'RUT',
  })

  const [espacioFechasData, setEspacioFechasData] = useState<EspacioFechasData>({
    tipoEspacioId: '',
    tipoEspacioNombre: '',
    fechaEntrada: '',
    fechaSalida: '',
    espaciosSeleccionados: [],
    tarifaBase: 0,
    unidadReserva: 'noche',
  })

  const [registrarPago, setRegistrarPago] = useState(false) // Checkbox para registrar pago

  const [pagoData, setPagoData] = useState<PagoData>({
    metodoPago: 'transferencia',
    numeroPago: '',
    fechaPago: new Date().toISOString().split('T')[0],
    montoPago: 0, // Se actualizará cuando se obtiene monto
    comprobante: null,
  })

  const [dteData, setDTEData] = useState<DTEData>({
    crearDTE: false,
    tipoDTE: 'boleta',
    numeroDTE: '',
    folioInterno: '',
    montoDTE: 0, // Se actualizará cuando se selecciona monto de pago
    fechaVencimiento: '',
  })

  // Estado para rastrear el pago creado (para mostrar comprobante en confirmación)
  const [createdPago, setCreatedPago] = useState<CreatedPagoInfo | null>(null)

  // Hooks
  const crearReserva = useCrearReservaManual()
  const { data: tiposEspacios = [], isLoading: cargandoTipos } = useTiposEspacios()

  // ✅ Estado para disparar búsqueda manualmente (al hacer click en botón)
  const [filtrosBusqueda, setFiltrosBusqueda] = useState<any>(null)

  // ✅ Hook para obtener disponibilidad - se ejecuta SOLO cuando filtrosBusqueda cambia (click en botón)
  const { data: espaciosDisponibles = [], isLoading: cargandoDisponibilidad } = useDisponibilidadSimple(filtrosBusqueda)

  // ✅ Calcular monto total de todos los espacios seleccionados
  const monto = espacioFechasData.espaciosSeleccionados.reduce((sum, esp) => sum + (esp.monto || 0), 0)

  // ✅ Con nuevo diseño: cada espacio valida sus propias fechas en su modal
  // No hay validación global de disponibilidad

  // ✅ NUEVO: Estado para modal de fechas (con espacio y disponibilidad precargados)
  const [modalEspacio, setModalEspacio] = useState<any>(null)

  // ✅ NUEVO: Handler para abrir modal con espacio disponible
  // Simula el comportamiento del click en "Reservar" de una tarjeta de espacio
  const handleAbrirModalEspacio = (espacio: any) => {
    setModalEspacio(espacio)
  }

  // ✅ NUEVO: Handler para cuando se seleccionan fechas de un espacio desde el modal
  const handleConfirmarFechasEspacio = (selectionData: any) => {
    if (!modalEspacio) return

    const espacioId = modalEspacio.id
    const espacioExistente = espacioFechasData.espaciosSeleccionados.find(e => e.id === espacioId)

    if (espacioExistente) {
      // Actualizar espacio existente con nuevas fechas
      setEspacioFechasData(prev => ({
        ...prev,
        espaciosSeleccionados: prev.espaciosSeleccionados.map(e =>
          e.id === espacioId
            ? {
                ...e,
                fechaEntrada: selectionData.fechaEntrada,
                fechaSalida: selectionData.fechaSalida,
                monto: selectionData.montoTotal || 0,
                desglose: selectionData.desglose,
                unidadReserva: selectionData.unidad_reserva || prev.unidadReserva,
              }
            : e
        ),
      }))
    } else {
      // Agregar nuevo espacio al carrito
      const nuevoEspacio: EspacioSeleccionado = {
        id: modalEspacio.id,
        nombre: modalEspacio.nombre,
        codigo: modalEspacio.codigo,
        tipoNombre: espacioFechasData.tipoEspacioNombre,
        tarifaBase: 0, // Tarifa base no disponible - el monto real viene del modal
        fechaEntrada: selectionData.fechaEntrada,
        fechaSalida: selectionData.fechaSalida,
        monto: selectionData.montoTotal || 0,
        desglose: selectionData.desglose,
        unidadReserva: selectionData.unidad_reserva || espacioFechasData.unidadReserva,
      }
      setEspacioFechasData(prev => ({
        ...prev,
        espaciosSeleccionados: [...prev.espaciosSeleccionados, nuevoEspacio],
      }))
    }

    setModalEspacio(null)
    toastSuccess(`✓ ${modalEspacio.nombre} agregado a la reserva`)
  }

  // Actualizar monto de pago cuando cambia el monto total
  useEffect(() => {
    if (monto > 0) {
      setPagoData(prev => ({
        ...prev,
        montoPago: monto,
      }))
      setDTEData(prev => ({
        ...prev,
        montoDTE: monto,
      }))
    }
  }, [monto])

  // Limpiar búsqueda cuando cambias de tipo de espacio
  useEffect(() => {
    setModalEspacio(null)
    setFiltrosBusqueda(null) // Limpiar disponibilidad anterior
  }, [espacioFechasData.tipoEspacioId])

  // Validación Step 1
  const isStep1Valid = () => {
    return (
      clienteData.nombre.trim() !== '' &&
      clienteData.email.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clienteData.email)
    )
  }

  // Validación Step 2
  const isStep2Valid = () => {
    // ✅ NUEVO: Validar que todos los espacios seleccionados tengan:
    // 1. Fechas de entrada y salida
    // 2. Monto > 0 (indicadora de disponibilidad validada)
    const todosEspaciosConFechas = espacioFechasData.espaciosSeleccionados.length > 0 &&
                                   espacioFechasData.espaciosSeleccionados.every(esp =>
                                     esp.fechaEntrada &&
                                     esp.fechaSalida &&
                                     esp.monto > 0
                                   )

    return todosEspaciosConFechas
  }

  // Validación Step 3
  const isStep3Valid = () => {
    // Si no se quiere registrar pago, el paso es válido sin hacer más validaciones
    if (!registrarPago) {
      return true
    }

    // Si se quiere registrar pago, todos los campos son requeridos
    return (
      pagoData.numeroPago.trim() !== '' &&
      pagoData.fechaPago !== '' &&
      pagoData.montoPago > 0 &&
      pagoData.montoPago <= monto
    )
  }

  // Validación Step 4
  const isStep4Valid = () => {
    if (!dteData.crearDTE) return true
    return (
      dteData.tipoDTE !== '' &&
      dteData.numeroDTE.trim() !== '' &&
      dteData.montoDTE > 0
    )
  }

  const handleNextStep = () => {
    if (step === 1 && !isStep1Valid()) {
      toastError('Por favor completa los datos del cliente')
      return
    }
    if (step === 2 && !isStep2Valid()) {
      toastError('Por favor selecciona espacio y fechas válidas')
      return
    }
    if (step === 3 && !isStep3Valid()) {
      if (pagoData.montoPago > monto) {
        toastError('El monto a pagar no puede exceder el monto total')
      } else if (pagoData.montoPago > 0 && !pagoData.numeroPago.trim()) {
        toastError('Por favor ingresa el número o referencia del pago')
      } else if (pagoData.montoPago > 0 && !pagoData.fechaPago) {
        toastError('Por favor ingresa la fecha del pago')
      } else {
        toastError('Por favor revisa los datos de pago')
      }
      return
    }
    if (step === 4 && !isStep4Valid()) {
      toastError('Por favor completa los datos del DTE si deseas generarlo')
      return
    }
    setStep(step + 1)
  }

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1)
  }

  const handleCreateReserva = async () => {
    if (!isStep1Valid() || !isStep2Valid() || !isStep3Valid() || !isStep4Valid()) {
      toastError('Por favor completa todos los campos requeridos')
      return
    }

    try {
      // ✅ Enviar múltiples espacios
      const espacioIdPrimero = espacioFechasData.espaciosSeleccionados[0]?.id || ''
      const espacios_ids = espacioFechasData.espaciosSeleccionados.map(e => e.id)

      // ✅ Calcular fecha_entrada y fecha_salida globales:
      // fecha_entrada: la más temprana de todos los espacios
      // fecha_salida: la más tardía de todos los espacios
      const fechasEntrada = espacioFechasData.espaciosSeleccionados.map(e => e.fechaEntrada).filter(f => f)
      const fechasSalida = espacioFechasData.espaciosSeleccionados.map(e => e.fechaSalida).filter(f => f)

      const fechaEntradaGlobal = fechasEntrada.length > 0 ? fechasEntrada.sort()[0] : ''
      const fechaSalidaGlobal = fechasSalida.length > 0 ? fechasSalida.sort().pop() : ''

      // ✅ Construir espacios_detalle con detalles individuales de cada espacio
      // Esto es lo que el backend necesita para NO recalcular tarifas
      const espacios_detalle = espacioFechasData.espaciosSeleccionados.map(esp => ({
        espacio_id: esp.id,
        fecha_entrada: esp.fechaEntrada, // Fecha individual de ESTE espacio
        fecha_salida: esp.fechaSalida, // Fecha individual de ESTE espacio
        tarifa_unitaria: esp.monto || 0, // Tarifa total para ESTE espacio (calculado en el modal)
      }))

      const response = await crearReserva.mutateAsync({
        cliente_nombre: clienteData.nombre,
        cliente_email: clienteData.email,
        cliente_telefono: clienteData.telefono || undefined,
        cliente_documento: clienteData.numero_documento || undefined,
        cliente_tipo_documento: clienteData.tipo_documento || undefined,
        espacio_id: espacioIdPrimero, // Primer espacio para backward compatibility
        espacios_ids: espacios_ids, // Array de todos los espacios
        fecha_entrada: fechaEntradaGlobal,
        fecha_salida: fechaSalidaGlobal,
        cantidad_huespedes: 0, // COUNT_GUESTS=False: no se usa
        monto_total: monto,
        monto_pago: registrarPago ? pagoData.montoPago : 0, // Solo enviar pago si se registró
        metodo_pago: registrarPago ? pagoData.metodoPago : undefined, // Solo enviar método si se registró
        referencia_pago: registrarPago ? pagoData.numeroPago : undefined, // ✅ Enviar referencia del pago
        comprobante_pago: undefined,
        // ✅ Enviar detalles de espacios para que backend NO recalcule
        espacios_detalle: espacios_detalle,
        crear_dte: registrarPago && dteData.crearDTE, // Solo crear DTE si se registró pago
        dte_tipo: registrarPago && dteData.crearDTE ? dteData.tipoDTE : undefined,
        dte_numero: registrarPago && dteData.crearDTE ? dteData.numeroDTE : undefined,
        dte_monto: registrarPago && dteData.crearDTE ? dteData.montoDTE : undefined, // Monto del DTE (puede diferir del pago)
      })

      // 📎 Si hay comprobante y se registró pago, subirlo ahora
      if (registrarPago && pagoData.comprobante && response?.pago_id) {
        try {
          const formData = new FormData()
          formData.append('file', pagoData.comprobante)

          await apiClient.post(
            `/api/admin-protected/pagos/${response.pago_id}/subir-comprobante`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              }
            }
          )

          toastSuccess('Comprobante subido exitosamente!')

          // Rastrear el pago creado con su comprobante para mostrar en confirmación
          setCreatedPago({
            id: response.pago_id,
            filename: pagoData.comprobante.name,
            mimeType: pagoData.comprobante.type,
            size: pagoData.comprobante.size,
            uploadDate: new Date().toISOString(),
          })
        } catch (uploadError) {
          console.error('Error subiendo comprobante:', uploadError)
          toastError('Reserva creada pero no se pudo subir el comprobante. Intenta nuevamente.')

          // Aún así registrar el pago (sin comprobante)
          setCreatedPago({
            id: response.pago_id,
          })
        }
      } else if (registrarPago && response?.pago_id) {
        // Si no hay comprobante pero sí hay pago registrado
        setCreatedPago({
          id: response.pago_id,
        })
      }

      // Reset form
      setStep(1)
      setClienteData({ nombre: '', email: '', telefono: '', numero_documento: '', tipo_documento: 'RUT' })
      setEspacioFechasData({
        tipoEspacioId: '',
        tipoEspacioNombre: '',
        fechaEntrada: '',
        fechaSalida: '',
        espaciosSeleccionados: [],
        tarifaBase: 0,
        unidadReserva: 'noche',
      })
      setPagoData({
        metodoPago: 'transferencia',
        numeroPago: '',
        fechaPago: new Date().toISOString().split('T')[0],
        montoPago: 0,
        comprobante: null,
      })
      setRegistrarPago(false) // Reset checkbox de pago
      setDTEData({
        crearDTE: false,
        tipoDTE: 'boleta',
        numeroDTE: '',
        folioInterno: '',
        montoDTE: 0,
        fechaVencimiento: '',
      })
    } catch (error) {
      console.error('Error creando reserva:', error)
    }
  }

  const progressWidth = (step / 5) * 100

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: colors.text }}>
          Crear Reserva Manual
        </h1>
        <p style={{ color: colors.textSecondary }}>
          Paso {step} de 5: {
            step === 1 ? 'Datos del Cliente' :
            step === 2 ? 'Espacio y Fechas' :
            step === 3 ? 'Pago' :
            step === 4 ? 'Documento Tributario' :
            'Confirmación'
          }
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8 bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${progressWidth}%`,
            backgroundColor: colors.primary,
          }}
        />
      </div>

      {/* Step 1: Cliente */}
      {step === 1 && (
        <div className={`space-y-4 ${deviceInfo.isMobile ? 'p-4' : 'p-6'}`} style={{ backgroundColor: colors.surface, borderRadius: '0.5rem' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>
            Información del Cliente
          </h2>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Nombre Completo *
            </label>
            <input
              type="text"
              value={clienteData.nombre}
              onChange={(e) => setClienteData({ ...clienteData, nombre: e.target.value })}
              placeholder="Nombre del cliente"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: colors.border,
                focusOutlineColor: colors.primary,
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Email *
            </label>
            <input
              type="email"
              value={clienteData.email}
              onChange={(e) => setClienteData({ ...clienteData, email: e.target.value })}
              placeholder="correo@ejemplo.com"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: colors.border,
                focusOutlineColor: colors.primary,
              }}
            />
          </div>

          <div className={`grid gap-4 ${deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                Teléfono
              </label>
              <input
                type="tel"
                value={clienteData.telefono}
                onChange={(e) => setClienteData({ ...clienteData, telefono: e.target.value })}
                placeholder="+56 9 1234 5678"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                style={{
                  borderColor: colors.border,
                  focusOutlineColor: colors.primary,
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                Tipo de Documento
              </label>
              <select
                value={clienteData.tipo_documento}
                onChange={(e) => setClienteData({ ...clienteData, tipo_documento: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                style={{
                  borderColor: colors.border,
                  focusOutlineColor: colors.primary,
                }}
              >
                <option value="RUT">RUT</option>
                <option value="Pasaporte">Pasaporte</option>
                <option value="Cédula">Cédula</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Número de Documento
            </label>
            <input
              type="text"
              value={clienteData.numero_documento}
              onChange={(e) => setClienteData({ ...clienteData, numero_documento: e.target.value })}
              placeholder="12.345.678-9"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: colors.border,
                focusOutlineColor: colors.primary,
              }}
            />
          </div>
        </div>
      )}

      {/* Step 2: Búsqueda de disponibilidad y selección de espacios */}
      {step === 2 && (
        <div className={`space-y-6 ${deviceInfo.isMobile ? 'p-4' : 'p-6'}`} style={{ backgroundColor: colors.surface, borderRadius: '0.5rem' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>
            Buscar Espacios Disponibles
          </h2>

          {/* 1. Tipo de Espacio */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Tipo de Espacio *
            </label>
            <select
              value={espacioFechasData.tipoEspacioId}
              onChange={(e) => {
                const tipoId = e.target.value
                const tipo = tiposEspacios.find((t: any) => t.id === tipoId)
                setEspacioFechasData(prev => ({
                  ...prev,
                  tipoEspacioId: tipoId,
                  tipoEspacioNombre: tipo?.nombre || '',
                  unidadReserva: tipo?.unidad_reserva || 'noche',
                  tarifaBase: tipo?.tarifa_base_tipo || 0,
                  // Limpiar búsqueda anterior al cambiar tipo
                  fechaEntrada: '',
                  fechaSalida: '',
                }))
              }}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{ borderColor: colors.border }}
              disabled={cargandoTipos}
            >
              <option value="">-- Selecciona un tipo --</option>
              {tiposEspacios.map((tipo: any) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* 2. Rango de Fechas (solo si tipo está seleccionado) */}
          {espacioFechasData.tipoEspacioId && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Fecha Entrada *
                </label>
                <input
                  type="date"
                  value={espacioFechasData.fechaEntrada}
                  onChange={(e) => {
                    setEspacioFechasData(prev => ({
                      ...prev,
                      fechaEntrada: e.target.value,
                    }))
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: colors.border }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Fecha Salida *
                </label>
                <input
                  type="date"
                  value={espacioFechasData.fechaSalida}
                  onChange={(e) => {
                    setEspacioFechasData(prev => ({
                      ...prev,
                      fechaSalida: e.target.value,
                    }))
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: colors.border }}
                />
              </div>
            </div>
          )}

          {/* 3. Botón Buscar Disponibilidad - dispara búsqueda al hacer click */}
          {espacioFechasData.tipoEspacioId && espacioFechasData.fechaEntrada && espacioFechasData.fechaSalida && (
            <button
              onClick={() => {
                // ✅ Validar que fecha_salida >= fecha_entrada (permite mismo día)
                if (espacioFechasData.fechaSalida < espacioFechasData.fechaEntrada) {
                  toastError('Fecha de salida debe ser igual o posterior a fecha de entrada')
                  return
                }

                // ✅ Disparar búsqueda al hacer click (no automáticamente)
                setFiltrosBusqueda({
                  tipo_espacio_id: espacioFechasData.tipoEspacioId,
                  fecha_entrada: espacioFechasData.fechaEntrada,
                  fecha_salida: espacioFechasData.fechaSalida,
                })
              }}
              disabled={cargandoDisponibilidad || espacioFechasData.fechaSalida < espacioFechasData.fechaEntrada}
              className="w-full px-6 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                if (!cargandoDisponibilidad) {
                  e.currentTarget.style.backgroundColor = colors.primaryDark
                }
              }}
              onMouseLeave={(e) => {
                if (!cargandoDisponibilidad) {
                  e.currentTarget.style.backgroundColor = colors.primary
                }
              }}
            >
              {cargandoDisponibilidad ? '⏳ Buscando disponibilidad...' : '🔍 Buscar Disponibilidad'}
            </button>
          )}

          {/* 4. Mostrar espacios disponibles */}
          {espaciosDisponibles.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
                ✅ {espaciosDisponibles.length} Espacio(s) Disponible(s)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {espaciosDisponibles.map((espacio: any) => (
                  <div
                    key={espacio.id}
                    className="p-4 border rounded-lg cursor-pointer transition-all hover:shadow-lg"
                    style={{
                      borderColor: colors.primary,
                      backgroundColor: `${colors.primary}05`,
                    }}
                    onClick={() => handleAbrirModalEspacio(espacio)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold" style={{ color: colors.text }}>
                          {espacio.nombre}
                        </h4>
                        <p style={{ color: colors.textSecondary, fontSize: '0.875rem' }}>
                          {espacio.codigo}
                        </p>
                      </div>
                    </div>
                    <p style={{ color: colors.textSecondary, fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      Click para ver disponibilidad y tarifas →
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 5. Mostrar carrito de espacios seleccionados */}
          {espacioFechasData.espaciosSeleccionados.length > 0 && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}15`, borderLeft: `4px solid ${colors.primary}` }}>
              <h3 className="font-semibold mb-3" style={{ color: colors.text }}>
                📋 Espacios en la Reserva ({espacioFechasData.espaciosSeleccionados.length})
              </h3>
              <div className="space-y-3">
                {espacioFechasData.espaciosSeleccionados.map((esp) => (
                  <div key={esp.id} className="p-3 bg-white rounded" style={{ borderLeft: `4px solid ${colors.primary}` }}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p style={{ color: colors.text }} className="font-medium">
                          {esp.nombre} ({esp.codigo})
                        </p>
                        <p style={{ color: colors.textSecondary, fontSize: '0.875rem' }}>
                          {esp.fechaEntrada && esp.fechaSalida
                            ? `📅 ${esp.fechaEntrada} → ${esp.fechaSalida}`
                            : '⚠️ Sin fechas'}
                        </p>
                      </div>
                      <p style={{ color: colors.primary, fontWeight: 'bold', fontSize: '1.125rem' }}>
                        ${esp.monto.toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          // Encontrar en disponibles y abrir modal para editar
                          const espacioEnDisponibles = espaciosDisponibles.find((e: any) => e.id === esp.id)
                          if (espacioEnDisponibles) {
                            handleAbrirModalEspacio(espacioEnDisponibles)
                          }
                        }}
                        className="flex-1 px-3 py-2 rounded text-sm font-semibold border transition-all"
                        style={{
                          borderColor: colors.primary,
                          color: colors.primary,
                          backgroundColor: 'transparent',
                        }}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => {
                          setEspacioFechasData(prev => ({
                            ...prev,
                            espaciosSeleccionados: prev.espaciosSeleccionados.filter(e => e.id !== esp.id),
                          }))
                          toastSuccess(`✓ ${esp.nombre} removido`)
                        }}
                        className="px-4 py-2 rounded text-sm font-semibold text-white transition-all"
                        style={{ backgroundColor: colors.primaryDark || colors.primary }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `2px solid ${colors.border}`, marginTop: '1rem', paddingTop: '1rem' }} className="flex justify-between items-center">
                <span style={{ color: colors.text }} className="font-bold">Total:</span>
                <span style={{ color: colors.primary }} className="text-xl font-bold">
                  ${monto.toLocaleString('es-CL')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Pago */}
      {step === 3 && (
        <div className={`space-y-4 ${deviceInfo.isMobile ? 'p-4' : 'p-6'}`} style={{ backgroundColor: colors.surface, borderRadius: '0.5rem' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>
            Pago
          </h2>

          {/* Checkbox para registrar pago */}
          <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" style={{
            borderColor: registrarPago ? colors.primary : colors.border,
            backgroundColor: registrarPago ? `${colors.primary}15` : 'transparent'
          }}>
            <input
              type="checkbox"
              checked={registrarPago}
              onChange={(e) => {
                setRegistrarPago(e.target.checked)
                // Si se desmarca, resetear datos de pago
                if (!e.target.checked) {
                  setPagoData({
                    metodoPago: 'transferencia',
                    numeroPago: '',
                    fechaPago: new Date().toISOString().split('T')[0],
                    montoPago: 0,
                    comprobante: null,
                  })
                }
              }}
              className="w-5 h-5"
            />
            <span className="ml-3 font-medium" style={{ color: colors.text }}>
              ¿Registrar información de pago?
            </span>
            <span className="ml-2 text-sm" style={{ color: colors.textSecondary }}>
              (Opcional - puedes pagar después)
            </span>
          </label>

          {/* Mostrar campos de pago solo si registrarPago = true */}
          {registrarPago && (
          <>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Monto Total de la Reserva
            </label>
            <div className="p-4 rounded-lg text-center" style={{ backgroundColor: `${colors.primary}15` }}>
              <p className="text-3xl font-bold" style={{ color: colors.primary }}>
                ${(monto || 0).toLocaleString('es-CL')}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Monto a Pagar (Puede ser Parcial) *
            </label>
            <input
              type="number"
              value={pagoData.montoPago}
              onChange={(e) => {
                const valor = Math.max(0, parseFloat(e.target.value) || 0)
                setPagoData({ ...pagoData, montoPago: valor })
                // Actualizar monto del DTE también si se cambia el pago
                setDTEData(prev => ({
                  ...prev,
                  montoDTE: prev.montoDTE === monto ? valor : prev.montoDTE
                }))
              }}
              min="0"
              max={monto}
              step="0.01"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: pagoData.montoPago > monto ? colors.warning : colors.border,
                focusOutlineColor: colors.primary,
              }}
            />
            <p style={{ color: colors.textSecondary, fontSize: '0.875rem', marginTop: '0.5rem' }}>
              {pagoData.montoPago > 0 && monto > 0 ? (
                <>Monto a pagar: ${pagoData.montoPago.toLocaleString('es-CL')} de ${monto.toLocaleString('es-CL')}</>
              ) : (
                <>Ingresa el monto a pagar (puedes pagar menos del total)</>
              )}
              {pagoData.montoPago > monto && (
                <span style={{ color: colors.warning, display: 'block', marginTop: '0.25rem' }}>
                  ⚠️ El monto a pagar no puede exceder el monto total
                </span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Método de Pago {pagoData.montoPago > 0 ? '*' : '(Opcional)'}
            </label>
            <div className="space-y-2">
              {(['transferencia', 'efectivo', 'cheque', 'tarjeta'] as const).map((metodo) => (
                <label key={metodo} className="flex items-center p-3 border rounded-lg cursor-pointer transition-all" style={{
                  borderColor: pagoData.metodoPago === metodo ? colors.primary : colors.border,
                  backgroundColor: pagoData.metodoPago === metodo ? `${colors.primary}15` : 'transparent',
                }}>
                  <input
                    type="radio"
                    name="metodo"
                    value={metodo}
                    checked={pagoData.metodoPago === metodo}
                    onChange={(e) => setPagoData({ ...pagoData, metodoPago: e.target.value as any })}
                  />
                  <span className="ml-3 capitalize" style={{ color: colors.text }}>
                    {metodo === 'transferencia' && '💳 Transferencia Bancaria'}
                    {metodo === 'efectivo' && '💵 Efectivo'}
                    {metodo === 'cheque' && '📋 Cheque'}
                    {metodo === 'tarjeta' && '💳 Pago con Tarjeta'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Número de Pago */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Número de Pago / Referencia {pagoData.montoPago > 0 ? '*' : '(Opcional)'}
            </label>
            <input
              type="text"
              value={pagoData.numeroPago}
              onChange={(e) => setPagoData({ ...pagoData, numeroPago: e.target.value })}
              placeholder="Ej: TRF-001234, CH-5678, etc."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: colors.border,
                focusOutlineColor: colors.primary,
              }}
            />
          </div>

          {/* Fecha de Pago */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Fecha de Pago {pagoData.montoPago > 0 ? '*' : '(Opcional)'}
            </label>
            <input
              type="date"
              value={pagoData.fechaPago}
              onChange={(e) => setPagoData({ ...pagoData, fechaPago: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
              style={{
                borderColor: colors.border,
                focusOutlineColor: colors.primary,
              }}
            />
          </div>

          {/* Comprobante de Pago */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              📎 Comprobante de Pago (Opcional)
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                if (file) {
                  // Validar tamaño (máx 10MB)
                  if (file.size > 10 * 1024 * 1024) {
                    toastError('El archivo no puede ser mayor a 10MB')
                    return
                  }
                  // Validar tipo
                  const typesPermitidos = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
                  if (!typesPermitidos.includes(file.type)) {
                    toastError('Tipo de archivo no permitido. Usa PDF, JPG, PNG, GIF o DOC.')
                    return
                  }
                }
                setPagoData({ ...pagoData, comprobante: file })
              }}
              className="w-full px-4 py-2 border-2 rounded-lg border-dashed focus:ring-2 focus:border-transparent"
              style={{
                borderColor: pagoData.comprobante ? colors.primary : colors.border,
                backgroundColor: pagoData.comprobante ? `${colors.primary}08` : 'transparent',
              }}
            />
            <p className="text-xs mt-2" style={{ color: colors.textSecondary }}>
              Tipos permitidos: PDF, JPG, PNG, GIF, DOC (máx 10MB)
            </p>
            {pagoData.comprobante && (
              <div className="mt-3 p-3 rounded-lg flex items-center justify-between" style={{
                backgroundColor: `${colors.primary}15`,
                borderLeft: `3px solid ${colors.primary}`
              }}>
                <p className="text-sm font-medium" style={{ color: colors.primary }}>
                  ✓ {pagoData.comprobante.name}
                </p>
                <p className="text-xs" style={{ color: colors.textSecondary }}>
                  {(pagoData.comprobante.size / 1024).toFixed(2)} KB
                </p>
              </div>
            )}
          </div>
          </>
          )}

          {/* Mensaje cuando no se registra pago */}
          {!registrarPago && (
            <div className="p-4 rounded-lg border-2" style={{
              borderColor: colors.border,
              backgroundColor: `${colors.primary}10`
            }}>
              <p style={{ color: colors.text }} className="font-medium">
                ✓ Puedes crear la reserva sin pago. El pago queda pendiente.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Step 4: DTE */}
      {step === 4 && (
        <div className={`space-y-4 ${deviceInfo.isMobile ? 'p-4' : 'p-6'}`} style={{ backgroundColor: colors.surface, borderRadius: '0.5rem' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>
            Información del Documento Tributario (DTE)
          </h2>

          <label className="flex items-center p-3 border rounded-lg cursor-pointer transition-all" style={{
            borderColor: colors.border,
            backgroundColor: dteData.crearDTE ? `${colors.primary}15` : 'transparent',
          }}>
            <input
              type="checkbox"
              checked={dteData.crearDTE}
              onChange={(e) => setDTEData({ ...dteData, crearDTE: e.target.checked })}
            />
            <span className="ml-3 font-medium" style={{ color: colors.text }}>
              Registrar información del DTE
            </span>
          </label>

          {dteData.crearDTE && (
            <div className="space-y-4 p-4 border rounded-lg" style={{ borderColor: colors.border, backgroundColor: `${colors.primary}05` }}>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Tipo de Documento *
                </label>
                <select
                  value={dteData.tipoDTE}
                  onChange={(e) => setDTEData({ ...dteData, tipoDTE: e.target.value as any })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{
                    borderColor: colors.border,
                    focusOutlineColor: colors.primary,
                  }}
                >
                  <option value="boleta">Boleta</option>
                  <option value="factura">Factura</option>
                  <option value="nota_credito">Nota de Crédito</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Número DTE *
                </label>
                <input
                  type="text"
                  value={dteData.numeroDTE}
                  onChange={(e) => setDTEData({ ...dteData, numeroDTE: e.target.value })}
                  placeholder="Ej: BL-0001234"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{
                    borderColor: colors.border,
                    focusOutlineColor: colors.primary,
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                  Monto del DTE *
                </label>
                <input
                  type="number"
                  value={dteData.montoDTE}
                  onChange={(e) => {
                    const valor = Math.max(0, parseFloat(e.target.value) || 0)
                    setDTEData({ ...dteData, montoDTE: valor })
                  }}
                  min="0"
                  step="0.01"
                  placeholder="Monto del documento tributario"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{
                    borderColor: colors.border,
                    focusOutlineColor: colors.primary,
                  }}
                />
                <p style={{ color: colors.textSecondary, fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {dteData.montoDTE !== pagoData.montoPago ? (
                    <>Monto del DTE: ${dteData.montoDTE.toLocaleString('es-CL')} (diferente al pago de ${pagoData.montoPago.toLocaleString('es-CL')})</>
                  ) : (
                    <>Monto del DTE: ${dteData.montoDTE.toLocaleString('es-CL')}</>
                  )}
                </p>
              </div>

              <div className={`grid gap-4 ${deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                    Folio Interno
                  </label>
                  <input
                    type="text"
                    value={dteData.folioInterno}
                    onChange={(e) => setDTEData({ ...dteData, folioInterno: e.target.value })}
                    placeholder="Folio interno"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{
                      borderColor: colors.border,
                      focusOutlineColor: colors.primary,
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                    Fecha de Vencimiento
                  </label>
                  <input
                    type="date"
                    value={dteData.fechaVencimiento}
                    onChange={(e) => setDTEData({ ...dteData, fechaVencimiento: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{
                      borderColor: colors.border,
                      focusOutlineColor: colors.primary,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 5: Confirmación */}
      {step === 5 && (
        <div className={`space-y-6 ${deviceInfo.isMobile ? 'p-4' : 'p-6'}`} style={{ backgroundColor: colors.surface, borderRadius: '0.5rem' }}>
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            Confirmar Reserva Manual
          </h2>

          <div className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}05`, borderLeft: `4px solid ${colors.primary}` }}>
              <h3 className="font-semibold mb-2" style={{ color: colors.text }}>Cliente</h3>
              <p style={{ color: colors.textSecondary }}>
                <strong>Nombre:</strong> {clienteData.nombre}
              </p>
              <p style={{ color: colors.textSecondary }}>
                <strong>Email:</strong> {clienteData.email}
              </p>
              {clienteData.telefono && (
                <p style={{ color: colors.textSecondary }}>
                  <strong>Teléfono:</strong> {clienteData.telefono}
                </p>
              )}
              {clienteData.tipo_documento && (
                <p style={{ color: colors.textSecondary }}>
                  <strong>Tipo de Documento:</strong> {clienteData.tipo_documento}
                </p>
              )}
              {clienteData.numero_documento && (
                <p style={{ color: colors.textSecondary }}>
                  <strong>Número de Documento:</strong> {clienteData.numero_documento}
                </p>
              )}
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}05`, borderLeft: `4px solid ${colors.primary}` }}>
              <h3 className="font-semibold mb-2" style={{ color: colors.text }}>Alojamiento</h3>
              {/* ✅ Mostrar tipos únicos de los espacios seleccionados */}
              {(() => {
                const tiposUnicos = [...new Set(espacioFechasData.espaciosSeleccionados.map(e => e.tipoNombre))]
                return (
                  <p style={{ color: colors.textSecondary }} className="mb-2">
                    <strong>Tipo(s):</strong> {tiposUnicos.join(', ')}
                  </p>
                )
              })()}
              <p style={{ color: colors.textSecondary }} className="mb-3">
                <strong>Espacios y Fechas:</strong>
              </p>
              {/* ✅ Mostrar cada espacio con SUS PROPIAS fechas */}
              <ul className="ml-4 space-y-2 mb-2">
                {espacioFechasData.espaciosSeleccionados.map((esp) => (
                  <li key={esp.id} style={{ color: colors.textSecondary }}>
                    <div className="mb-1">
                      • {esp.nombre} ({esp.codigo}) - {esp.tipoNombre}
                    </div>
                    <div style={{ marginLeft: '1rem', fontSize: '0.875rem', color: colors.primary }}>
                      📅 {esp.fechaEntrada} → {esp.fechaSalida}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}05`, borderLeft: `4px solid ${colors.primary}` }}>
              <h3 className="font-semibold mb-3" style={{ color: colors.text }}>Monto de la Reserva</h3>

              {/* Desglose por espacio */}
              <div className="space-y-2 mb-3">
                {espacioFechasData.espaciosSeleccionados.map((esp) => (
                  <div key={esp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.5rem', borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textSecondary }}>{esp.nombre}</span>
                    <span style={{ color: colors.primary, fontWeight: 'bold' }}>
                      ${esp.monto.toLocaleString('es-CL')}
                    </span>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.5rem', borderTop: `2px solid ${colors.primary}` }}>
                <span style={{ color: colors.text }} className="font-bold">Total:</span>
                <span className="text-2xl font-bold" style={{ color: colors.primary }}>
                  ${monto.toLocaleString('es-CL')}
                </span>
              </div>
            </div>

            {registrarPago && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}05`, borderLeft: `4px solid ${colors.primary}` }}>
                <h3 className="font-semibold mb-2" style={{ color: colors.text }}>Pago</h3>
                <div className="space-y-2">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: colors.textSecondary }}>Monto Total de Reserva:</span>
                    <span className="font-semibold" style={{ color: colors.text }}>
                      ${monto.toLocaleString('es-CL')}
                    </span>
                  </div>
                  {pagoData.montoPago !== monto && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: colors.primary }}>Monto a Pagar:</span>
                      <span className="text-lg font-bold" style={{ color: colors.primary }}>
                        ${pagoData.montoPago.toLocaleString('es-CL')}
                      </span>
                    </div>
                  )}
                  {pagoData.montoPago !== monto && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: colors.warning }}>Saldo Pendiente:</span>
                      <span style={{ color: colors.warning, fontWeight: 'bold' }}>
                        ${(monto - pagoData.montoPago).toLocaleString('es-CL')}
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: '1rem', paddingTop: '1rem' }}>
                  <p style={{ color: colors.textSecondary }}>
                    <strong>Método:</strong> {
                      pagoData.metodoPago === 'transferencia' ? 'Transferencia' :
                      pagoData.metodoPago === 'efectivo' ? 'Efectivo' :
                      pagoData.metodoPago === 'cheque' ? 'Cheque' :
                      'Pago con Tarjeta'
                    }
                  </p>
                  <p style={{ color: colors.textSecondary }}>
                    <strong>Número/Referencia:</strong> {pagoData.numeroPago}
                  </p>
                  <p style={{ color: colors.textSecondary }}>
                    <strong>Fecha de Pago:</strong> {parseLocalDateString(pagoData.fechaPago).toLocaleDateString('es-CL')}
                  </p>
                </div>
              </div>
            )}

            {registrarPago && dteData.crearDTE && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: `${colors.primary}05`, borderLeft: `4px solid ${colors.primary}` }}>
                <h3 className="font-semibold mb-2" style={{ color: colors.text }}>Documento Tributario</h3>
                <div className="space-y-2">
                  <p style={{ color: colors.textSecondary }}>
                    <strong>Tipo:</strong> {dteData.tipoDTE.charAt(0).toUpperCase() + dteData.tipoDTE.slice(1)}
                  </p>
                  <p style={{ color: colors.textSecondary }}>
                    <strong>Número:</strong> {dteData.numeroDTE}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: colors.textSecondary }}>Monto DTE:</span>
                    <span className="font-semibold" style={{ color: colors.primary }}>
                      ${dteData.montoDTE.toLocaleString('es-CL')}
                    </span>
                  </div>
                  {dteData.montoDTE !== pagoData.montoPago && (
                    <p style={{ color: colors.warning, fontSize: '0.875rem' }}>
                      ℹ️ Monto diferente al pago: ${pagoData.montoPago.toLocaleString('es-CL')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {registrarPago && createdPago && (
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <h3 className="font-semibold mb-3" style={{ color: colors.text }}>📎 Comprobante de Pago</h3>
                {createdPago.mimeType ? (
                  <ComprobanteViewer
                    pagoId={createdPago.id}
                    filename={createdPago.filename}
                    mimeType={createdPago.mimeType}
                    size={createdPago.size}
                    uploadDate={createdPago.uploadDate}
                    showMetadata={true}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-xs text-gray-600">
                      📭 No hay comprobante
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-4 rounded-lg" style={{ backgroundColor: colors.warning + '15', borderLeft: `4px solid ${colors.warning}` }}>
            <p style={{ color: colors.warning, fontSize: '0.875rem' }}>
              ⚠️ Verifica que todos los datos sean correctos antes de crear la reserva.
            </p>
          </div>
        </div>
      )}

      {/* ✅ Modal de Fechas - abre con espacio disponible precargado */}
      {modalEspacio && (
        <DateAvailabilityModal
          espacio={modalEspacio}
          isOpen={!!modalEspacio}
          onClose={() => {
            setModalEspacio(null)
          }}
          onSelectDates={(selectionData: any) => {
            // selectionData viene de DateAvailabilityModal con fechas y desglose
            handleConfirmarFechasEspacio(selectionData)
          }}
          rangoOriginal={{
            // Usar el rango que el operador buscó
            fecha_entrada: espacioFechasData.fechaEntrada,
            fecha_salida: espacioFechasData.fechaSalida,
          }}
        />
      )}

      {/* Botones de navegación */}
      <div className={`flex gap-3 mt-8 ${deviceInfo.isMobile ? 'flex-col-reverse' : 'justify-between'}`}>
        <button
          onClick={handlePrevStep}
          disabled={step === 1}
          className="px-6 py-2 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: '#f3f4f6',
            color: colors.primary,
            border: `1px solid ${colors.border}`,
          }}
          onMouseEnter={(e) => {
            if (step > 1) e.currentTarget.style.backgroundColor = '#e5e7eb'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6'
          }}
        >
          ← Anterior
        </button>

        {step < 5 ? (
          <button
            onClick={handleNextStep}
            disabled={
              (step === 1 && !isStep1Valid()) ||
              (step === 2 && !isStep2Valid()) ||
              (step === 3 && !isStep3Valid()) ||
              (step === 4 && !isStep4Valid())
            }
            className="px-6 py-2 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            Siguiente →
          </button>
        ) : (
          <button
            onClick={handleCreateReserva}
            disabled={crearReserva.isPending}
            className="px-6 py-2 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            {crearReserva.isPending ? '⏳ Creando...' : '✓ Crear Reserva'}
          </button>
        )}
      </div>
    </div>
  )
}

export default CreateReservaManualPage
